// src/contexts/AdminContext.js
import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  useRef,
} from "react";
import axios from "axios";
import { AuthContext } from "./AuthContext";

export const AdminContext = createContext();
export const DashboardContext = AdminContext;

// 🔴 UBAH KE true jika backend siap
const API_READY = true;

export const AdminProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext);

  const API_BASE = "";
  // =======================
  // STATE
  // =======================
  const [sectors, setSectors] = useState([]);
  const [machines, setMachines] = useState([]);
  const [users, setUsers] = useState([]);
  const [tools, setTools] = useState([]);
  // Catatan: 'maintenanceHistory' dihapus dari context — tidak pernah di-fetch
  // dan tidak dikonsumsi siapa pun. Admin MaintenanceTab mengambil history
  // sendiri via GET /api/maintenance/history.
  const [stockTools, setStockTools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [lastUpdateTime, setLastUpdateTime] = useState(null);

  const isInitialLoadRef = useRef(true);

  // =======================
  // AUTH CONFIG
  // =======================
  const getConfig = () => {
    const token = localStorage.getItem("token");
    return {
      headers: {
        "Content-Type": "application/json",
        ...(token && { "x-auth-token": token }),
      },
    };
  };

  // =======================
  // FETCH FUNCTIONS
  // =======================
  const fetchSectors = useCallback(async () => {
    if (!API_READY) return;
    try {
      const res = await axios.get(`${API_BASE}/api/sectors`, getConfig());

      const normalized = (res.data || []).map((s) => ({
        _id: String(s.id),
        id: s.id, // optional tapi berguna
        name: s.name,
        leader: s.leader,
        machine_count: s.machine_count,
      }));

      setSectors(normalized);
    } catch (err) {
      console.error("Fetch sectors error:", err);
    }
  }, [API_BASE]);

  const fetchMachines = useCallback(async () => {
    if (!API_READY) return;
    try {
      const res = await axios.get(`${API_BASE}/api/machines`, getConfig());
      const normalized = (res.data || []).map((m) => ({
        ...m,
        sector: m.sector
          ? { _id: String(m.sector._id ?? m.sector.id), name: m.sector.name }
          : null,
      }));
      setMachines(normalized);
    } catch (err) {
      console.error("Fetch machines error:", err.response?.data || err.message);
    }
  }, [API_BASE]);

  const fetchUsers = useCallback(async () => {
    if (!API_READY) return;
    try {
      const res = await axios.get(`${API_BASE}/api/users`, getConfig());
      setUsers(res.data || []);
    } catch (err) {
      console.error("Fetch users error:", err.response?.data || err.message);
    }
  }, [API_BASE]);

  const fetchTools = useCallback(async () => {
    if (!API_READY) return;
    try {
      const res = await axios.get(`${API_BASE}/api/tools`, getConfig());
      setTools(res.data || []);
    } catch (err) {
      console.error("Fetch tools error:", err.response?.data || err.message);
    }
  }, [API_BASE]);

  const fetchStockTools = useCallback(async () => {
    if (!API_READY) return;
    try {
      const res = await axios.get(`${API_BASE}/api/stock-tools`, getConfig());
      setStockTools(res.data?.data || []);
    } catch (err) {
      console.error(
        "Fetch stock tools error:",
        err.response?.data || err.message,
      );
    }
  }, [API_BASE]);

  const fetchData = useCallback(async () => {
    if (!API_READY) return;

    try {
      setLoading(true);
      setError("");

      await Promise.all([
        fetchSectors(),
        fetchMachines(),
        fetchUsers(),
        fetchTools(),
        fetchStockTools(),
      ]);

      setLastUpdateTime(new Date());
      isInitialLoadRef.current = false;
    } catch (err) {
      console.error("Fetch data error:", err);
      setError("Gagal mengambil data admin");
    } finally {
      setLoading(false);
    }
  }, [fetchSectors, fetchMachines, fetchUsers, fetchTools, fetchStockTools]);

  // =======================
  // INITIAL LOAD
  // =======================
  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser, fetchData]);

  // =======================
  // CRUD OPERATIONS
  // =======================
  // --- Sectors ---
  const addSector = async (data) => {
    try {
      await axios.post(`${API_BASE}/api/sectors`, data, getConfig());
      await fetchSectors();
      return ok("Sektor berhasil ditambahkan");
    } catch (err) {
      console.error("Add sector error:", err.response?.data || err.message);
      return fail(err.response?.data?.message || "Gagal menambahkan sektor");
    }
  };
  const updateSector = async (id, data) => {
    try {
      await axios.put(`${API_BASE}/api/sectors/${id}`, data, getConfig());
      await fetchSectors();
      return ok("Sektor berhasil diperbarui");
    } catch (err) {
      console.error("Update sector error:", err.response?.data || err.message);
      return fail(err.response?.data?.message || "Gagal memperbarui sektor");
    }
  };
  const deleteSector = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/sectors/${id}`, getConfig());
      setSectors((prev) => prev.filter((s) => s._id !== id));
      return ok("Sektor berhasil dihapus");
    } catch (err) {
      console.error("Delete sector error:", err.response?.data || err.message);
      return fail(err.response?.data?.message || "Gagal menghapus sektor");
    }
  };

  const normalizeMachine = (machine, sectorId = null) => {
    // Sektor bisa datang dalam beberapa bentuk: id langsung, { _id }, { id },
    // atau machine.sector_id. Resolve id-nya dulu, lalu cari di daftar sektor.
    const resolvedSectorId =
      sectorId ??
      machine?.sector?._id ??
      machine?.sector?.id ??
      machine?.sector_id ??
      (typeof machine?.sector === "string" || typeof machine?.sector === "number"
        ? machine.sector
        : null);

    const sectorObj = sectors.find(
      (s) => String(s._id) === String(resolvedSectorId),
    );

    return {
      ...machine,
      sector: sectorObj
        ? { _id: String(sectorObj._id), name: sectorObj.name }
        : // Fallback: pertahankan objek sektor dari server (punya name) bila ada,
          // supaya kolom Sektor tidak kosong saat difilter per sektor.
          machine?.sector && machine.sector.name
          ? {
              _id: String(machine.sector._id ?? machine.sector.id ?? ""),
              name: machine.sector.name,
            }
          : null,
    };
  };

  const fetchMachinesBySector = async (sectorId) => {
    if (!sectorId) {
      await fetchMachines();
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(
        `${API_BASE}/api/machines/sector/${sectorId}`,
        getConfig(),
      );

      const normalized = (res.data || []).map((m) => normalizeMachine(m));

      setMachines(normalized);
    } finally {
      setLoading(false);
    }
  };

  const addMachine = async (payload) => {
    try {
      const sectorId = String(payload.sector_id);

      const exists = sectors.some((s) => String(s._id) === sectorId);

      if (!exists) {
        return {
          success: false,
          error: "Sector tidak valid, silakan pilih sector yang ada",
        };
      }

      payload.sector_id = sectorId;

      const res = await axios.post(
        `${API_BASE}/api/machines`,
        payload,
        getConfig(),
      );

      const newMachine = normalizeMachine(res.data.machine, sectorId);

      setMachines((prev) => [...prev, newMachine]);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || "Gagal menambahkan mesin",
      };
    }
  };
  const updateMachine = async (id, payload) => {
    try {
      if (payload.sector_id) {
        const exists = sectors.some(
          (s) => String(s._id) === String(payload.sector_id),
        );

        if (!exists) {
          return { success: false, error: "Sector tidak valid" };
        }
      }

      const res = await axios.put(
        `${API_BASE}/api/machines/${id}`,
        payload,
        getConfig(),
      );

      const updatedMachine = normalizeMachine(
        res.data.machine,
        payload.sector_id,
      );

      setMachines((prev) =>
        prev.map((m) => (m._id === id ? updatedMachine : m)),
      );

      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.message || "Gagal memperbarui mesin",
      };
    }
  };

  const deleteMachine = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/machines/${id}`, getConfig());
      await fetchMachines();
      return ok("Mesin berhasil dihapus");
    } catch (err) {
      console.error("Delete machine error:", err.response?.data || err.message);
      return fail(err.response?.data?.message || "Gagal menghapus mesin");
    }
  };

  const addTool = async (data) => {
    try {
      await axios.post(`${API_BASE}/api/tools`, data, getConfig());
      await fetchTools();
      return ok("Tool berhasil ditambahkan");
    } catch (err) {
      console.error("Add tool error:", err.response?.data || err.message);
      return fail(err.response?.data?.message || "Gagal menambahkan tool");
    }
  };

  const updateTool = async (id, data) => {
    try {
      await axios.put(`${API_BASE}/api/tools/${id}`, data, getConfig());

      // ✅ AMBIL ULANG DATA DARI DB
      await fetchTools();

      return ok("Tool berhasil diperbarui");
    } catch (err) {
      console.error("Update tool error:", err.response?.data || err.message);
      return fail(err.response?.data?.message || "Gagal memperbarui tool");
    }
  };

  const deleteTool = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/tools/${id}`, getConfig());
      await fetchTools();
      return ok("Tool berhasil dihapus");
    } catch (err) {
      console.error("Delete tool error:", err.response?.data || err.message);
      return fail(err.response?.data?.message || "Gagal menghapus tool");
    }
  };

  //stock Tools//
  const addStockTool = async (data) => {
    try {
      await axios.post(`${API_BASE}/api/stock-tools`, data, getConfig());
      await fetchStockTools();
      return ok("Stock tool berhasil ditambahkan");
    } catch (err) {
      console.error("Add stock tool error:", err.response?.data || err.message);
      return fail("Gagal menambahkan stock tool");
    }
  };

  const updateStockTool = async (id, data) => {
    try {
      await axios.put(`${API_BASE}/api/stock-tools/${id}`, data, getConfig());
      await fetchStockTools();
      return ok("Stock tool berhasil diperbarui");
    } catch (err) {
      console.error(
        "Update stock tool error:",
        err.response?.data || err.message,
      );
      return fail("Gagal memperbarui stock tool");
    }
  };

  const deleteStockTool = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/stock-tools/${id}`, getConfig());
      await fetchStockTools();
      return ok("Stock tool berhasil dihapus");
    } catch (err) {
      console.error(
        "Delete stock tool error:",
        err.response?.data || err.message,
      );
      return fail("Gagal menghapus stock tool");
    }
  };

  // --- Users ---
  const addUser = async (data) => {
    try {
      await axios.post(`${API_BASE}/api/users`, data, getConfig());
      await fetchUsers();
      return ok("User berhasil ditambahkan");
    } catch (err) {
      console.error("Add user error:", err.response?.data || err.message);
      return fail(err.response?.data?.message || "Gagal menambahkan user");
    }
  };
  const updateUser = async (id, data) => {
    try {
      await axios.put(`${API_BASE}/api/users/${id}`, data, getConfig());
      await fetchUsers();
      return ok("User berhasil diperbarui");
    } catch (err) {
      console.error("Update user error:", err.response?.data || err.message);
      return fail(err.response?.data?.message || "Gagal memperbarui user");
    }
  };
  const deleteUser = async (id) => {
    try {
      await axios.delete(`${API_BASE}/api/users/${id}`, getConfig());
      await fetchUsers();
      return ok("User berhasil dihapus");
    } catch (err) {
      console.error("Delete user error:", err.response?.data || err.message);
      return fail(err.response?.data?.message || "Gagal menghapus user");
    }
  };

  // =======================
  // HELPERS
  // =======================
  const ok = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(""), 3000);
    return { success: true };
  };
  const fail = (msg) => {
    setError(msg);
    return { success: false, error: msg };
  };
  const getMachineStats = () => ({
    operational: machines.filter((m) => m.status === "Operational").length,
    needMaintenance: machines.filter((m) => m.status === "Need Maintenance")
      .length,
    underMaintenance: machines.filter((m) => m.status === "Under Maintenance")
      .length,
    offline: machines.filter((m) => m.status === "Offline").length,
    total: machines.length,
  });

  return (
    <AdminContext.Provider
      value={{
        sectors,
        machines,
        users,
        tools,
        stockTools,
        loading,
        error,
        success,
        lastUpdateTime,
        fetchData,

        // sectors
        addSector,
        updateSector,
        deleteSector,

        // machines
        fetchMachinesBySector,
        addMachine,
        updateMachine,
        deleteMachine,
        fetchMachines,

        // tools
        fetchTools,
        addTool,
        updateTool,
        deleteTool,

        //stock tools//
        addStockTool,
        updateStockTool,
        deleteStockTool,

        // users
        addUser,
        updateUser,
        deleteUser,

        getMachineStats,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
